import { Box, Button, Container, Group, Image, Menu, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useOrg } from '@/auth/OrgContext';
import { Eyebrow } from '@/components/Eyebrow';
import { OrgSwitcher } from '@/features/orgs/OrgSwitcher';
import { brand, forest, shadows } from '@/theme/theme';

const pillStyle = {
  borderRadius: 999,
  backgroundColor: 'rgba(255,255,255,0.08)',
  border: `1px solid ${brand.onDarkLine}`,
};

export interface ClientShellProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  cta?: { label: string; onClick: () => void };
  children?: ReactNode;
}

/** Client-mood page shell: dark textured hero header, then content pulled up over its edge. See frontend-design.md §5.1. */
export function ClientShell({ eyebrow, title, lead, cta, children }: ClientShellProps) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const { orgs, current, select } = useOrg();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    await navigate({ to: '/login' });
  };

  return (
    <Box bg={brand.bg} mih="100vh">
      <Box pos="relative" bg={forest[9]} style={{ overflow: 'hidden' }}>
        <Box
          pos="absolute"
          inset={0}
          style={{ backgroundImage: 'url(/rastr.png)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.28 }}
        />
        <Container size="lg" px={32} pos="relative">
          <Group justify="space-between" py="md" style={{ borderBottom: `1px solid ${brand.onDarkLine}` }}>
            <Image src="/logo-white.png" alt={t('app.name')} h={40} w="auto" fit="contain" />
            <Group gap="sm">
              <OrgSwitcher orgs={orgs} current={current} onSelect={select} isStaff={me?.is_staff} />
              {me?.is_staff && (
                <UnstyledButton component={Link} to="/staff" px={12} py={6} style={pillStyle}>
                  <Text fz={14} fw={600} c={brand.onDark}>
                    {t('app.staff')}
                  </Text>
                </UnstyledButton>
              )}
              <Menu shadow="md" width={200} position="bottom-end">
                <Menu.Target>
                  <UnstyledButton px={12} py={6} style={pillStyle}>
                    <Text fz={14} fw={600} c={brand.onDark}>
                      {me?.full_name || me?.email}
                    </Text>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item component={Link} to="/assistant-access">
                    {t('nav.assistantAccess')}
                  </Menu.Item>
                  <Menu.Item onClick={() => void handleSignOut()}>{t('nav.signOut')}</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
          <Stack py={64} gap="sm" maw={640}>
            {eyebrow && <Eyebrow onDark>{eyebrow}</Eyebrow>}
            <Title order={1} c={brand.onDark} style={{ fontSize: 48, lineHeight: 1.1 }}>
              {title}
            </Title>
            {lead && (
              <Text c={brand.onDarkMuted} fz={16}>
                {lead}
              </Text>
            )}
            {cta && (
              <Button
                mt="sm"
                onClick={cta.onClick}
                style={{ alignSelf: 'flex-start', backgroundColor: brand.yellow, color: brand.dark, boxShadow: shadows.cta }}
              >
                {cta.label}
              </Button>
            )}
          </Stack>
        </Container>
      </Box>
      <Container size="lg" px={32} mt={-64} pb={64}>
        {children}
      </Container>
    </Box>
  );
}
